import {
  ChangeEvent,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { UseTextFieldParams } from "./TextField.types";

export const useTextField = <R>({
  ref,
  defaultValue,
  value,
  onChangeEvent,
  onChange,
}: UseTextFieldParams<R>) => {
  const textfieldRef = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<string>(
    (value ?? defaultValue ?? "") as unknown as string
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(
    ref,
    (() => {
      return {
        instance: textfieldRef.current,
        value: state,
        setValue: (v: string) => {
          setState(v);
        },
        reset: () => {
          setState("");
        },
      };
    }) as unknown as () => R,
    [state]
  );

  const onChangeHandler = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onChangeEvent?.(event);
    const newValue = event.target.value;
    if (typeof value === "undefined") {
      setState(newValue);
    }
    onChange?.(newValue);
  };

  const onClearStateHandler = () => {
    if (typeof value === "undefined") {
      setState("");
    }
    onChange?.("");
  };

  const valueState = useMemo(
    () => (typeof value !== "undefined" ? value : state) as string,
    [value, state]
  );

  return {
    textfieldRef,
    valueState,
    onChangeHandler,
    onClearStateHandler,
  };
};
